import { PDFDocument } from 'pdf-lib';
import * as uuid from 'uuid';
import { PDFFile, RESTRICTED_DOMAINS, stateAbbreviationToFullName, stateZipCodes } from './constant';
import * as Yup from 'yup';
import { parse, isValid } from 'date-fns';
import { apiInstance } from 'src/api/apiInstance';
import { JobMailingTypes, LegacyMailingTypes } from 'src/pages/dashboard/util';
import { OrganizationType } from './enums/organization-type.enum';

export const generateFileData = async (file: File, pageCount: number, type?: string | null, isOCRValid?: boolean) => {
  const name = file.name.replace('pdf', '');

  let base64string = (await toBase64(file)) as string;
  return {
    id: uuid.v4(),
    name: name,
    data: URL.createObjectURL(file),
    pageCount,
    documentDescription: type === 'ballot' ? 'Ballot' : type === 'proofOfClaim' ? 'Proof of Claim' : '',
    documentECFDocketNumber: '',
    baseName: file.name,
    baseSize: file.size,
    baseBuffer: base64string,
    ...(isOCRValid !== undefined ? { isOCRValid } : {}),
    ...(type === 'ballot' ? { paperColor: 'standard' } : {}),
  };
};

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

//custom debounce function for user input fields
export const debounce = <T extends (...args: any[]) => void>(
  func: T,
  delay: number,
): ((...args: Parameters<T>) => void) => {
  let timerId: NodeJS.Timeout | undefined;

  return (...args: Parameters<T>) => {
    if (timerId) {
      clearTimeout(timerId);
    }
    timerId = setTimeout(() => func(...args), delay);
  };
};

export const getPageSizes = async (file: File): Promise<number> => {
  const arrayBuffer = await file.arrayBuffer();

  const pdfDoc = await PDFDocument.load(arrayBuffer);

  return pdfDoc.getPageCount();
};

export const getUploadedFilesPageSize = async (file: PDFFile): Promise<number> => {
  try {
    // Fetch the createObjectURL to make it a blob
    const response = await fetch(file.data);

    // Check if the fetch was successful
    if (!response.ok) {
      throw new Error('Failed to fetch the file');
    }

    // Get the file as a Blob
    const blob = await response.blob();

    const pdfDoc = await PDFDocument.load(await blob.arrayBuffer());

    // Return the page count
    return pdfDoc.getPageCount();
  } catch (error) {
    console.error('Error loading PDF:', error);
    return 0;
  }
};

export const getCurrentYear = (): number => new Date().getFullYear();

export const getCurrentDate = () => {
  const currentDate = new Date();

  const year = currentDate.getFullYear();
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0');
  const day = currentDate.getDate().toString().padStart(2, '0');

  const formattedDate = `${year}-${month}-${day}`;

  return formattedDate;
};

export const getCurrentTime = () => {
  const currentDate = new Date();

  const hours = currentDate.getHours().toString().padStart(2, '0');
  const minutes = currentDate.getMinutes().toString().padStart(2, '0');
  const seconds = currentDate.getSeconds().toString().padStart(2, '0');

  const formattedTime = `${hours}:${minutes}:${seconds}`;

  return formattedTime;
};

export const dateValidationTest = Yup.string().test({
  name: 'date-validation',
  skipAbsent: true, // Skip the test if the field is absent
  test(value, ctx) {
    if (!value) {
      return false; // Ensures that required error is triggered if value is empty
    }

    // Check if the value matches the MM/DD/YYYY format
    const formatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
    if (!formatRegex.test(value)) {
      return ctx.createError({ message: 'Date must be in MM/DD/YYYY format.' });
    }

    // Parse the date and validate its correctness
    const parsedDate = parse(value, 'MM/dd/yyyy', new Date());
    if (!isValid(parsedDate)) {
      return ctx.createError({ message: 'Date is invalid.' });
    }

    // Ensure the year is not in the future
    const currentYear = new Date().getFullYear();
    const inputYear = parsedDate.getFullYear();
    if (inputYear > currentYear) {
      return ctx.createError({ message: 'Year cannot be in the future.' });
    }

    return true;
  },
});

export const isObjectEmpty = (obj: Record<string, any>) => {
  if (!obj) {
    return true;
  }

  const allFieldsEmpty = Object.values(obj).every((value) => value === null || value === undefined || value === '');

  if (allFieldsEmpty) {
    return true;
  }

  return false;
};

const getPTHour = (now: Date = new Date()): number => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    hourCycle: 'h23',
    hour: 'numeric',
  });
  return Number(formatter.formatToParts(now).find((p) => p.type === 'hour')?.value);
};

export const checkIsBetweenFourPmAndMidnightPT = () => {
  const hour = getPTHour();
  return hour >= 16 && hour <= 23;
};

export const isBetweenNoonAndFourPmPT = () => {
  const hour = getPTHour();
  return hour >= 12 && hour < 16;
};

export const checkForInvalidDomains = (domains: any[]): string[] => {
  return domains
    .map((domain) => domain.name)
    .filter((domain) => RESTRICTED_DOMAINS.includes(domain.split('.')[0]) || !/^[\w-]+\.\w+$/.test(domain));
};

export const downloadFile = async (endpoint: string, filename: string) => {
  try {
    const response = await apiInstance.get(endpoint, { responseType: 'blob' });
    const contentType = response.headers['content-type'];
    let downloadFilename = filename;
    const contentDisposition = response.headers['content-disposition'];
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        downloadFilename = filenameMatch[1].replace(/['"]/g, '').split('.')[0];
      }
    }

    const mimeToExt: Record<string, string> = {
      'application/zip': 'zip',
      'application/pdf': 'pdf',
      'image/png': 'png',
      'image/jpeg': 'jpg',
      'text/plain': 'txt',
      'text/csv': 'csv',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    };

    const extension = mimeToExt[contentType];

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${downloadFilename}.${extension}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    const errorData = await parseBlobError(err);
    throw new Error(errorData.message);
  }
};

const parseBlobError = async (error: any) => {
  if (error.response && error.response.data instanceof Blob) {
    try {
      const errorText = await error.response.data.text();
      return JSON.parse(errorText);
    } catch (parseErr) {
      console.error('Failed to parse error response:', parseErr);
      return { message: 'Unknown error occurred' };
    }
  }
  return { message: error.message || 'Request failed' };
};

export const getPasswordErrorTypes = (password: string): string[] => {
  const errors = [];

  if (password.length < 8) {
    errors.push('length');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('lowercase');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('uppercase');
  }
  if (!/[@$!%*?&_#^()+=\/\\,.'":;{}[\]<>`~|-]/.test(password)) {
    errors.push('specialChar');
  }
  if (!/\d/.test(password)) {
    errors.push('number');
  }

  return errors;
};

export const isValidDate = (dateString: string) => {
  const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateString.match(regex);

  if (!match) return false;

  const month = parseInt(match[1], 10);
  const day = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

export const transformIsoDateToUSAFormat = (isoDateStr: string) => {
  const date = new Date(isoDateStr);

  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const yyyy = date.getUTCFullYear();

  return `${mm}/${dd}/${yyyy}`;
};

export const isJobCancelable = (jobCreatedAt: string) => {
  return Date.now() - new Date(jobCreatedAt).getTime() <= 15 * 60 * 1000;
};

export const formatDateForFiltering = (date: string, type: 'start' | 'end') => {
  const dateObject = new Date(date);

  if (type === 'start') {
    dateObject.setHours(0, 0, 0, 0);
  }
  if (type === 'end') {
    dateObject.setHours(23, 59, 59, 999);
  }

  const tzoffset = dateObject.getTimezoneOffset() * 60000;
  return new Date(dateObject.valueOf() - tzoffset).toISOString().slice(0, -1);
};

export const isValidZipCode = (state: string, zipCode: string) => {
  return stateZipCodes[
    stateAbbreviationToFullName[state as keyof typeof stateAbbreviationToFullName] as keyof typeof stateZipCodes
  ].some((range) => +zipCode >= range[0] && +zipCode <= range[1]);
};

export const mapMultipleCaseNumbers = (
  items: { id: string; textfieldValue: string; value: string }[],
  response: { [key: string]: string[] },
) => {
  const valueToIdMap = Object.fromEntries(items.map((item) => [item.value, item.id]));

  const result: { [key: string]: string[] } = {};

  for (const [value, data] of Object.entries(response)) {
    const id = valueToIdMap[value] || 'mainCaseNumber';
    if (id) {
      result[id] = data;
    }
  }

  return result;
};

export const summarizeMailingTypes = (jobMailingTypes: JobMailingTypes | null | undefined) => {
  const mailingTypes: Partial<JobMailingTypes> = jobMailingTypes ?? {};
  const count = (mailingType: keyof JobMailingTypes) => mailingTypes[mailingType] ?? 0;
  const summarizedMailingTypes = {
    Domestic: count('first_class_domestic'),
    Certified: count('certified_domestic'),
    'Priority Express':
      count('priority_express_domestic') +
      count('priority_express_canada') +
      count('priority_express_mexico') +
      count('priority_express_international'),
    Mexico: count('first_class_mexico') + count('certified_mexico'),
    Canada: count('first_class_canada') + count('certified_canada'),
    International: count('first_class_international') + count('certified_international'),
    'CM/ECF': count('cm_ecf'),
  };

  return Object.entries(summarizedMailingTypes)
    .filter(([_, value]) => value !== 0)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};

export const summarizeLegacyMailingTypes = (jobMailingTypes: any) => {
  const mailingTypes = jobMailingTypes ?? {};
  const count = (mailingType: keyof LegacyMailingTypes) => mailingTypes[mailingType] ?? 0;
  const summarizedMailingTypes = {
    Domestic: count('first_class') + count('first_class_priority'),
    Certified: count('certified'),
    'Priority Express': count('first_class_overnight') + count('international_overnight'),
    Mexico: count('mexico') + count('mexico_priority'),
    Canada: count('canada') + count('canada_priority'),
    International: count('international') + count('international_priority'),
    'CM/ECF': count('cm_ecf'),
  };
  return Object.entries(summarizedMailingTypes)
    .filter(([_, value]) => value !== 0)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ');
};

export const formatCaseNumber = (caseNumber: string): string => {
  try {
    const bkFormatRegex = /^(\d{1}):(\d{2})-bk-(\d{5})$/i;
    const bkMatch = caseNumber.match(bkFormatRegex);

    if (bkMatch) {
      return caseNumber;
    }

    let strippedCaseNumber: string[] = [];
    const input = caseNumber.toLowerCase();

    if (input.includes('bk')) {
      const parts = input.split('bk');
      const beforeBk = parts[0] || '';
      const afterBk = parts[1] || '';

      const numbersBefore = extractNumbers(beforeBk);
      const numbersAfter = extractNumbers(afterBk);
      if (numbersAfter.length === 4) numbersAfter.unshift('0');

      strippedCaseNumber = [...numbersBefore, ...numbersAfter];
    } else {
      strippedCaseNumber = extractNumbers(input);
    }

    if (strippedCaseNumber.length < 7 || strippedCaseNumber.length > 8) {
      return caseNumber;
    }

    const digits = strippedCaseNumber.join('');
    const positions: { [key: number]: string } = {};

    for (let i = 0; i < digits.length; i++) {
      const positionNumber = i + 1;
      const digitIndex = digits.length - 1 - i;
      positions[positionNumber] = digits[digitIndex];
    }

    const bkFormat = `${positions[8] ? `${positions[8]}:` : ''}${positions[7] || '0'}${positions[6] || '0'}-bk-${positions[5] || '0'}${positions[4] || '0'}${positions[3] || '0'}${positions[2] || '0'}${positions[1] || '0'}`;

    return bkFormat;
  } catch (error) {
    return caseNumber;
  }
};

export const extractNumbers = (input: string) => {
  const numbers: string[] = [];

  for (let i = 0; i < input.length; i++) {
    const char = input[i];

    if (/\d/.test(char)) {
      numbers.push(char);
    }
    if (/[a-zA-Z]/.test(char)) {
      break;
    }
  }

  return numbers;
};

export const checkIsWeekend = () => {
  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short',
  })
    .formatToParts(new Date())
    .find((p) => p.type === 'weekday')?.value;
  return weekday === 'Sat' || weekday === 'Sun';
};

export const areCaseNumbersUnique = (arr: string[]) => new Set(arr).size === arr.length;

export const pxToRem = (px: number): number => px / 12.8;

const normalizeAddressLine = (line: string | null | undefined): string =>
  (line ?? '').trim().replace(/,/g, '').split('-')[0].toLowerCase();

const isSameAddress = (a: any, b: any): boolean => {
  const nameA = (a.name ?? '').trim().toLowerCase();
  const nameB = (b.name ?? '').trim().toLowerCase();
  if (nameA !== nameB) return false;

  const addressA = (a.address ?? []).map(normalizeAddressLine).filter((line: string) => line?.length);
  const addressB = (b.address ?? []).map(normalizeAddressLine).filter((line: string) => line?.length);
  if (addressA.length === 0 || addressB.length === 0) return false;

  const [shorter, longer] = addressA.length <= addressB.length ? [addressA, addressB] : [addressB, addressA];
  return shorter.every((line: string) => longer.includes(line));
};

export const mapRecipients = (caseParties: any[], creditors: any[]): [any[], any[]] => {
  // Deduplicate CM/ECF entries across all sources: MML creditors, user-supplied, and case parties.
  // When the same address appears multiple times as CM/ECF, one entry is kept with all mailing
  // flags merged. Non-CM/ECF entries are never deduplicated and always pass through as-is.
  const deduplicatedCmEcf: any[] = [];

  const addIfNew = (entry: any) => {
    const alreadyExists = deduplicatedCmEcf.some((existing) => isSameAddress(existing, entry));
    if (!alreadyExists) {
      deduplicatedCmEcf.push({ ...entry });
    }
  };

  for (const creditor of creditors.filter((c) => c.isCmEcf)) addIfNew(creditor);
  for (const caseParty of caseParties.filter((p) => p.isCmEcf)) addIfNew(caseParty);

  return [
    [...deduplicatedCmEcf.filter((p) => p.source === 'case_parties'), ...caseParties.filter((p) => !p.isCmEcf)],
    [...deduplicatedCmEcf.filter((c) => c.source !== 'case_parties'), ...creditors.filter((c) => !c.isCmEcf)],
  ];
};
// fallback for old pricing model
export const normalizeInvoice = (invoice: any) => {
  return {
    ...invoice,
    postageAndDelivery: invoice.postageAndDelivery ?? invoice.uspsPostage,
  };
};
